import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

interface Lender {
  name: string;
  email: string;
  hasEmail: boolean;
}

interface LenderSearchProps {
  onSelect: (lenderName: string, lenderEmail?: string) => void;
  selectedLender: string;
  disabled?: boolean;
}

const LenderSearch: React.FC<LenderSearchProps> = ({
  onSelect,
  selectedLender,
  disabled = false,
}) => {
  const { apiRequest, isAdmin, hasRole } = useAuth();
  // Searching lenders is open to any authenticated user (the Create Claim form
  // needs it), but POST /ui/admin/lenders is still gated to lender managers.
  // Don't offer general staff an action the server will refuse.
  const canManageLenders = isAdmin || hasRole('lender-manager');
  // initialise from any pre-existing selection (e.g. editing a drafted form)
  const [searchTerm, setSearchTerm] = useState(selectedLender || '');
  const [results, setResults] = useState<Lender[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLenderName, setNewLenderName] = useState('');
  const [newLenderEmail, setNewLenderEmail] = useState('');
  const [addingLender, setAddingLender] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevSelectedRef = useRef(selectedLender);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setShowAddForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync the input ONLY when the parent's selection actually changes to a new
  // lender (e.g. selection made elsewhere). Never re-fill while the user is
  // editing/clearing the box — the old `!searchTerm` guard made it impossible
  // to backspace away a selected lender (it kept snapping back).
  useEffect(() => {
    if (selectedLender !== prevSelectedRef.current) {
      prevSelectedRef.current = selectedLender;
      if (selectedLender) {
        setSearchTerm(selectedLender);
      }
    }
  }, [selectedLender]);

  // Debounced search function
  const performSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiRequest<{
        lenders: Lender[];
        totalCount: number;
        filteredCount: number;
      }>({
        method: 'GET',
        url: `/ui/admin/lenders?search=${encodeURIComponent(term)}`,
      });
      // Guard: apiRequest is configured with `validateStatus: status < 500`,
      // so a 4xx (e.g. 403/404) resolves here instead of throwing — arriving
      // as an error body with no `lenders` array. Setting results to that
      // undefined value previously crashed the whole page on the next render
      // (`results.map` on undefined -> blank screen). Fail safe instead.
      if (Array.isArray(response?.lenders)) {
        setResults(response.lenders);
        setShowDropdown(true);
      } else {
        setResults([]);
        setShowDropdown(false);
        setError('Failed to load lenders');
      }
    } catch (err: any) {
      console.error('Error searching lenders:', err);
      setError('Failed to search lenders');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest]);

  // Handle input change with debouncing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Editing the text invalidates any previously clicked lender — tell the
    // parent so the form doesn't silently keep the stale selection.
    if (selectedLender && value !== selectedLender) {
      onSelect('', '');
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer (300ms)
    debounceTimerRef.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // Handle lender selection
  const handleSelectLender = (lender: Lender) => {
    setSearchTerm(lender.name);
    onSelect(lender.name, lender.email);
    setShowDropdown(false);
    setResults([]);
  };

  // Handle "Add New Lender" click
  const handleAddNewClick = () => {
    setShowAddForm(true);
    setNewLenderName(searchTerm || '');
    setNewLenderEmail('');
    setFormError(null);
  };

  // Handle adding a new lender
  const handleAddLender = async () => {
    if (!newLenderName.trim()) {
      setFormError('Lender name is required');
      return;
    }

    if (!newLenderEmail.trim()) {
      setFormError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(newLenderEmail)) {
      setFormError('Please enter a valid email address');
      return;
    }

    setAddingLender(true);
    setFormError(null);

    try {
      const response = await apiRequest<{
        lender?: { name: string; email: string };
        message?: string;
        error?: string;
      }>({
        method: 'POST',
        url: '/ui/admin/lenders',
        data: {
          name: newLenderName.trim(),
          email: newLenderEmail.trim(),
        },
      });

      // Guard: same trap as performSearch above. apiRequest is configured with
      // `validateStatus: status < 500`, so a non-2xx (403 from the
      // lender-manager gate, 409 duplicate, 400 validation) RESOLVES here with
      // an error body instead of throwing. Running the success path on that
      // closed the form and reported the lender as selected while nothing was
      // created — the claim then saved a lender name absent from the canonical
      // list, and lender matching plus LOC routing failed silently downstream.
      // The route returns the created lender on 201, so require it.
      if (!response?.lender?.name) {
        setFormError(response?.error || response?.message || 'Failed to add lender');
        return;
      }

      // Select the newly created lender
      setSearchTerm(newLenderName.trim());
      onSelect(newLenderName.trim(), newLenderEmail.trim());
      setShowAddForm(false);
      setShowDropdown(false);
      setNewLenderName('');
      setNewLenderEmail('');
    } catch (err: any) {
      console.error('Error adding lender:', err);
      setFormError(err.message || 'Failed to add lender');
    } finally {
      setAddingLender(false);
    }
  };

  // Handle cancel adding
  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNewLenderName('');
    setNewLenderEmail('');
    setFormError(null);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="lender-search" ref={dropdownRef}>
      <div className="lender-search-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="lender-search-input"
          placeholder="Search for a lender..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => searchTerm && setShowDropdown(true)}
          disabled={disabled}
        />
        {isLoading && <span className="search-loading-indicator">Loading...</span>}
      </div>

      {error && <div className="lender-search-error">{error}</div>}

      {showDropdown && !showAddForm && (
        <ul className="lender-search-dropdown">
          {results.map((lender) => (
            <li
              key={lender.name}
              className="lender-search-item"
              onClick={() => handleSelectLender(lender)}
            >
              <div className="lender-item-name">{lender.name}</div>
              {lender.email && (
                <div className="lender-item-email">{lender.email}</div>
              )}
              {!lender.hasEmail && (
                <div className="lender-item-no-email">No email configured</div>
              )}
            </li>
          ))}
          {searchTerm && results.length === 0 && !isLoading && (
            <li className="lender-search-no-results-item">
              No lenders found matching "{searchTerm}"
            </li>
          )}
          {canManageLenders && (
            <li className="lender-search-add-new" onClick={handleAddNewClick}>
              <span className="add-icon">+</span> Add New Lender
            </li>
          )}
        </ul>
      )}

      {showAddForm && (
        <div className="lender-add-form">
          <h4>Add New Lender</h4>

          <div className="form-group">
            <label htmlFor="newLenderName">Lender Name *</label>
            <input
              id="newLenderName"
              type="text"
              value={newLenderName}
              onChange={(e) => setNewLenderName(e.target.value)}
              placeholder="e.g., BMW Finance"
              disabled={addingLender}
            />
          </div>

          <div className="form-group">
            <label htmlFor="newLenderEmail">Email Address *</label>
            <input
              id="newLenderEmail"
              type="email"
              value={newLenderEmail}
              onChange={(e) => setNewLenderEmail(e.target.value)}
              placeholder="e.g., complaints@lender.com"
              disabled={addingLender}
            />
          </div>

          {formError && <div className="form-error">{formError}</div>}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddLender}
              disabled={addingLender}
            >
              {addingLender ? 'Adding...' : 'Add Lender'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancelAdd}
              disabled={addingLender}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LenderSearch;
